/**
 * **열쇠 어댑터 팩토리** — `keyCtx` 를 만든다 (PR 7-b).
 *
 * 설계: `backend/docs/design/vault_sweep_keys/` (spec §2.2 K6)
 *
 * 🛑 **이 파일은 `pUtils` 를 import 한다 — 그래서 `sweepRunner` 가 이것을 import 하면 안 된다.**
 * `pUtils.js:10` 이 `env` 를 부르고 그 체인이 `env → service → conf → window` 로 이어져
 * node 환경 테스트를 로드 단계에서 죽인다(0.5단계 실측). 이 모듈을 쓰는 것은
 * **`redux/user/sagas.js` 뿐**이고, 그 파일은 이미 `pUtils` 를 import 한다(`sagas.js:12`).
 *
 * 🛑 **어댑터는 `rsKeyObj` 와 같은 모양이다** — `decrypt`/`encrypt`
 * (`pUtils.js:1378`·`:1395`). 그래야 러너가 리소스와 열쇠를 분기 없이 다룬다.
 */
import { encryptAESByKey, decryptAESByKey, decryptFromBase64 } from 'pUtils'
import { encryptVaultBlob, kdfForKind, VAULT_WRITE_FORMAT_KEY } from 'vaultWriteFormat'
import { getTempEnvParam } from 'env'
import UserService from 'api/Users'
import ResourceService from 'api/Resource'
import forge from 'node-forge'

/**
 * 저장한 값을 **API 로 다시 받아** 복호되는지 본다 (설계 K8).
 *
 * 🛑 재조회를 서버 안에서 하면 `get_db()` 의 SELECT 가 reader 로 라우팅되어
 * (`backend/database/database.py:78-92`) 직전 커밋을 못 읽는다 — 검증이 통과해도 아무것도
 * 증명하지 못한다. 그래서 반드시 클라이언트가 왕복한다.
 *
 * 🛑 **복호 성공만으로 판정한다.** 값이 같은지는 보지 않는다 — 재암호화는 매번 다른 IV 를
 * 쓰므로 암호문 비교는 항상 불일치다. 우리가 물어야 하는 것은 *"이 열쇠로 다시 열리는가"* 다.
 */
const makeVerifier = (ctx, fetchCurrent) => async () => {
  const current = await fetchCurrent()
  if (!current) return false
  const plain = await ctx.decrypt(current)
  return typeof plain === 'string' && plain.length > 0
}

/**
 * 키 재료 하나로 어댑터를 만든다.
 *
 * 🛑 **kdf 는 `kdfForKind`(`vaultWriteFormat.js`)가 정한다 — 여기서 판단하지 않는다.**
 * 라이터(`pUtils.js` `registerGroup`·`changePW` 등)와 **같은 표**를 봐야 한다. 갈리면
 * 같은 컬럼에 두 규격이 섞이고, 그것이 `backend/docs/design/group_key_kdf/` 가 고친 버그다.
 * (1회차 PR 리뷰 minor — 정책이 두 곳에 독립 존재하던 것을 정본 하나로 좁혔다.)
 */
export const makeKeyCtx = (rawMaterial, kind) => {
  if (!rawMaterial || !kind) return null
  const kdf = kdfForKind(kind)
  return {
    kind,
    rawMaterial,
    decrypt: async (b64) => decryptAESByKey(forge.util.decode64(b64), rawMaterial),
    // 🛑 **역방향 v1 암호화는 여기에 두지 않는다** (PR #2017 리뷰 minor).
    //    `reverseTool.js` 가 `rewriteV2ToV1` 의 thunk 를 만드는 유일한 곳이다. 여기에도
    //    같은 것을 두면 "base64 를 씌우지 않는다" 는 계약의 정본이 둘이 되고, 한쪽만
    //    고치는 순간 **어떤 키로도 복호되지 않는 값**이 저장된다(실측: `v1 decrypt failed`).
    encrypt: async (plain) =>
      forge.util.encode64(
        await encryptVaultBlob(
          getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
          async () => encryptAESByKey(plain, rawMaterial),
          { plaintext: plain, kdf, keyMaterial: rawMaterial },
        ),
      ),
  }
}

/**
 * `user_key` 공급원 — 사용자당 **1건**(`user_key.py:27` `user_id` UNIQUE).
 *
 * `prev`·`row_id` 는 **로그인 응답에서 온 같은 스냅샷**이다(`pUtils.js:642-644`).
 */
export const makeUserKeySource = (rsaObj) => {
  if (rsaObj == null || !rsaObj.encPrivateKeyBase64 || !rsaObj.userKeyId) return null
  const ctx = makeKeyCtx(rsaObj.UMK(), 'user_key')
  if (ctx == null) return null
  return {
    table: 'user_key',
    row_id: rsaObj.userKeyId,
    prev: rsaObj.encPrivateKeyBase64,
    ctx,
    verify: makeVerifier(ctx, async () => {
      const res = await UserService.me()
      return res?.data?.user_key
    }),
  }
}

/**
 * `group_key` 공급원 — bulk 레코드 하나에서.
 *
 * 🛑 **`group_private_key` 가 `GroupKey.key` 다.** 이름 직관이 정반대다 —
 * `backend/api/group.py:71`·`:114` 가 `GroupKey.key` → `enc_private_key`,
 * `UserGroup.key` → `enc_group_key` 로 라벨하고 응답 모델이 둘을 한 객체에 담는다
 * (`api/response/models/resource.py:151-155`). **엉뚱한 컬럼을 재암호화하면 그 그룹 전원이
 * 리소스 접근을 잃는다**(spec G2).
 */
export const makeGroupKeySourceFrom = (rsaObj) => (record) => {
  const gk = record?.group_key
  if (gk == null || !gk.group_key_id || !gk.group_private_key || !gk.user_group_key) return null
  if (rsaObj == null || !rsaObj.privateKeyPem) return null

  // groupKey = 내 개인키로 RSA 언랩한 `UserGroup.key` (`pUtils.js:1447` 과 같은 경로)
  const groupKey = decryptFromBase64(gk.user_group_key, rsaObj.privateKeyPem)
  const ctx = makeKeyCtx(groupKey, 'group_key')
  if (ctx == null) return null

  return {
    table: 'group_key',
    row_id: gk.group_key_id,
    prev: gk.group_private_key, // ← GroupKey.key. `user_group_key` 가 아니다
    ctx,
    verify: makeVerifier(ctx, async () => {
      const res = await ResourceService.get({ id: record.id })
      return res?.data?.group_key?.group_private_key
    }),
  }
}
